import { isOriginInspectionDocument, isTransportDocument, type DocumentType } from './document-types';
import { originInspectionFieldsFor, originInspectionItemFieldsFor } from '../trade/origin-inspection-fields';
import { transportFieldsFor, transportItemFieldsFor } from '../trade/transport-fields';
import { insuranceFields } from '../trade/insurance-fields';
import type { TradeInput, TradeItemInput } from '../trade/schema';

type InputField = Exclude<keyof TradeInput, 'buyer' | 'supplier' | 'items'>
  | `buyer.${keyof TradeInput['buyer']}` | `supplier.${keyof TradeInput['supplier']}` | `items.${keyof TradeItemInput}`;
type FieldGroup = Readonly<{ label: string; fields: readonly InputField[] }>;
const group = (label: string, ...fields: InputField[]): FieldGroup => ({ label, fields });
const buyer = ['buyer.name', 'buyer.attention', 'buyer.address', 'buyer.country'] as const;
const name = ['items.name', 'items.description'] as const;
const bank = group('收款银行信息（银行名/账户名/账号/地址/SWIFT/IBAN）', 'bankAccountVersionId');
const price = group('币种/单价', 'currency', 'items.unitPrice');
const quantity = group('数量/单位', 'items.quantity', 'items.unit');
const signature = group('授权签名', 'authorizedSignature');
const terms = group('贸易条款/指定地点', 'incoterm', 'namedPlace');
const rowRemark = group('商品行备注', 'items.remark');
const description = group('商品名/品名规格', ...name);
const model = group('型号', 'items.model');

// The same groups drive the initial guide and bound all follow-up questions.
// Required/conditional checks remain in validateTrade and its domain validators.
const groups: Record<DocumentType, readonly FieldGroup[]> = {
  QUOTATION: [
    group('客户名称', 'buyer.name'), group('联系人', 'buyer.attention'), group('报价币种', 'currency'),
    group('商品名', 'items.name'), model, group('商品图片（可选）', 'items.pictureDataUri'),
    group('数量', 'items.quantity'), group('单位', 'items.unit'), group('单价', 'items.unitPrice'), rowRemark,
    group('贸易条款', 'incoterm'), group('付款方式/条件', 'paymentTerms', 'paymentMethod'),
    group('报价有效期', 'validityDate'), group('单据备注', 'note'), signature,
  ],
  PROFORMA_INVOICE: [
    group('买方/收货人及地址', ...buyer), description, model, quantity, price, rowRemark, terms,
    group('付款方式/条件', 'paymentTerms', 'paymentMethod'), group('交货期', 'deliveryDate'),
    group('有效期', 'validityDate'), bank, group('单据备注', 'note'), signature,
  ],
  PURCHASE_ORDER: [
    group('供应商名称/负责人/地址/国家', 'supplier.name', 'supplier.attention', 'supplier.address', 'supplier.country'),
    group('交货条款', 'deliveryTerms'), description, model, quantity, price, rowRemark,
    group('交货期', 'deliveryDate'), group('付款条件', 'paymentTerms'), group('运输方式', 'shippingMethod'),
    group('唛头', 'shippingMarks'), group('买方签字', 'authorizedSignature'),
  ],
  SALES_CONTRACT: [
    group('卖方/买方', ...buyer), description, model, quantity, price, rowRemark,
    group('装运条款', 'shippingTerms'), group('付款条款', 'paymentClause'), group('保险条款', 'insuranceClause'),
    group('单据要求', 'documentsRequired'), group('检验条款', 'inspectionClause'), group('不可抗力条款', 'forceMajeureClause'),
    group('索赔条款', 'claimsClause'), group('仲裁条款', 'arbitrationClause'), group('卖方/买方签字盖章', 'authorizedSignature'),
  ],
  SHIPPING_ORDER: [
    group('船名/航次', 'vessel', 'voyageNumber'), group('起运港/目的港', 'portOfLoading', 'portOfDischarge'),
    group('发货人', 'shipper'), group('收货人', 'consignee'), group('通知人', 'notifyParty'), group('商品名/品名', ...name),
    group('唛头', 'items.marks'), group('件数/包装', 'items.packages'), group('毛重', 'items.grossWeight'),
    group('体积', 'items.measurement'), group('柜型柜量', 'items.container'), group('货物备注', 'items.remark'),
    group('运费条款', 'freightTerms'), group('装期 ETD', 'etd'), group('截关期', 'cutOff'),
    group('特殊要求', 'specialRequirements'), group('船代签字', 'carrierSignature'),
  ],
  COMMERCIAL_INVOICE: [
    group('买方名称/地址/电话', ...buyer), group('发票号/日期', 'invoiceNumber', 'invoiceDate'),
    group('合同号', 'contractNumber'), group('信用证号', 'lcNumber'), group('付款方式/条件', 'paymentTerms', 'paymentMethod'),
    group('船名/航次', 'vessel', 'voyageNumber'), group('起运港/目的港', 'portOfLoading', 'portOfDischarge'),
    group('ETD/ETA', 'etd', 'eta'), group('原产国', 'countryOfOrigin'), description, group('HS 编码', 'items.hsCode'),
    quantity, price, rowRemark, terms, group('运费/保费/其他费用/折扣', 'freight', 'insurance', 'otherCharges', 'discount'), bank, signature,
  ],
  PACKING_LIST: [
    group('收货人名称/地址/电话', 'consignee'), group('发票号/日期', 'invoiceNumber', 'invoiceDate'),
    group('合同号', 'contractNumber'), group('信用证号', 'lcNumber'), group('船名/航次', 'vessel', 'voyageNumber'),
    group('起运港/目的港', 'portOfLoading', 'portOfDischarge'), group('ETD/ETA', 'etd', 'eta'), description,
    group('唛头', 'items.marks'), quantity, group('包装/件数', 'items.packages'), group('箱号', 'items.cartonNumber'),
    group('毛重', 'items.grossWeight'), group('净重', 'items.netWeight'), group('体积', 'items.measurement'), signature,
  ],
  CUSTOMS_INVOICE: [
    group('买方/进口商名称/地址/电话', ...buyer), group('发票号/日期', 'invoiceNumber', 'invoiceDate'),
    group('原产国', 'countryOfOrigin'), group('目的国', 'destinationCountry'), terms, group('币种/汇率', 'currency', 'exchangeRate'),
    description, group('HS 编码', 'items.hsCode'), quantity, group('单价/总价', 'items.unitPrice'), rowRemark,
    group('FOB/运费/保费', 'freight', 'insurance'), bank, signature,
  ],
  BENEFICIARY_CERTIFICATE: [
    group('开证行名称/地址', 'issuingBankName', 'issuingBankAddress'), group('信用证号', 'lcNumber'),
    group('发票号', 'invoiceNumber'), group('提单号', 'billOfLadingNumber'), group('日期', 'invoiceDate'),
    group('船名/航次', 'vessel', 'voyageNumber'), group('装运日期', 'shipmentDate'),
    group('已寄单据清单', 'dispatchedDocuments'), group('受益人声明原文', 'certificateStatement'),
    group('装运后寄单天数', 'dispatchDays'), group('代表受益人', 'beneficiaryRepresentative'), group('授权签字', 'authorizedSignature'),
  ],
  BILL_OF_LADING: [
    group('托运人名称/地址/电话', 'shipper'), group('收货人名称/地址/电话', 'consignee'), group('通知方名称/电话', 'notifyParty'),
    group('提单号/签发日期/签发地', 'billOfLadingNumber', 'transportDate', 'placeOfIssue'),
    group('船名/航次', 'vessel', 'voyageNumber'), group('起运港/卸货港', 'portOfLoading', 'portOfDischarge'),
    group('交货地/最终目的地', 'placeOfDelivery', 'finalDestination'), group('整票唛头', 'shippingMarks'),
    group('货物明细（品名/件数/包装/毛重/净重/体积）', ...name, 'items.quantity', 'items.packages', 'items.grossWeight', 'items.netWeight', 'items.measurement'),
    group('柜号/封号', 'items.container', 'items.sealNumber'), group('运费条款', 'freightTerms'), group('正本份数', 'originals'),
    group('出单类型（正本/电放/海运单）', 'blServiceType'), group('备注', 'note'), group('承运人签章', 'carrierSignature'),
  ],
  AIR_WAYBILL: [
    group('空运单号/签发日期', 'awbNumber', 'transportDate'), group('始发机场/目的机场', 'departureAirport', 'destinationAirport'),
    group('航班号/日期', 'flightNumber', 'flightDate'), group('托运人名称/地址/电话', 'shipper'),
    group('收货人名称/地址/电话', 'consignee'), group('通知方名称/电话', 'notifyParty'), group('运费币种', 'currency'),
    group('货物明细（件数/毛重/体积/品名规格/计费重量）', ...name, 'items.quantity', 'items.grossWeight', 'items.measurement', 'items.chargeableWeight'),
    group('运输声明价值', 'declaredValueCarriage'), group('海关声明价值', 'declaredValueCustoms'), group('操作信息', 'handlingInformation'),
    group('运费代码/预付/到付', 'weightChargeCode', 'weightPrepaid', 'weightCollect'),
    group('其他费用代码/预付/到付', 'otherChargeCode', 'otherPrepaid', 'otherCollect'),
    group('税费代码/预付/到付', 'taxChargeCode', 'taxPrepaid', 'taxCollect'), group('承运人签章', 'carrierSignature'),
  ],
  MANIFEST: [
    group('日期', 'transportDate'), group('船名/航次', 'vessel', 'voyageNumber'), group('起运港/卸货港', 'portOfLoading', 'portOfDischarge'),
    group('ETD/ETA', 'etd', 'eta'), group('航次类型（出口/进口）', 'voyageType'), group('船公司/船长', 'carrierName', 'masterName'),
    group('货物明细（提单号/托运人/收货人/品名/件数/毛重/体积/柜号/封号/唛头）', 'items.billOfLadingNumber', 'items.shipper', 'items.consignee', ...name, 'items.quantity', 'items.grossWeight', 'items.measurement', 'items.container', 'items.sealNumber', 'items.marks'),
    group('申报声明', 'manifestDeclaration'), group('编制人', 'preparedBy'), group('承运人签章', 'carrierSignature'),
  ],
  SHIPPING_ADVICE: [
    group('收货人/通知对象', 'consignee'), group('日期', 'transportDate'), group('我方编号', 'ourReference'),
    group('发票号', 'invoiceNumber'), group('提单号', 'billOfLadingNumber'), group('合同号', 'contractNumber'),
    group('船名/航次', 'vessel', 'voyageNumber'), group('实际离港 ETD/预计到港 ETA', 'etd', 'eta'),
    group('起运港/目的港', 'portOfLoading', 'portOfDischarge'), group('原产国', 'countryOfOrigin'), group('币种', 'currency'),
    group('货物明细（品名/HS 编码/数量/单位/单价/金额/包装/毛重/净重/体积/唛头）', ...name, 'items.hsCode', 'items.quantity', 'items.unit', 'items.unitPrice', 'items.packages', 'items.grossWeight', 'items.netWeight', 'items.measurement', 'items.marks'),
    group('已寄单据/寄单说明', 'dispatchedDocuments'), group('快递单号', 'courierNumber'), group('寄出日期', 'dispatchDate'),
    group('备注', 'note'), group('签发人', 'authorizedSignature'),
  ],
  SHIPPING_INSTRUCTION: [
    group('货代/船公司及电话', 'forwarder'), group('订舱号', 'bookingNumber'), group('日期', 'transportDate'),
    group('托运人名称/地址/电话', 'shipper'), group('收货人名称/地址/电话', 'consignee'), group('通知方', 'notifyParty'),
    group('船名/航次', 'vessel', 'voyageNumber'), group('起运港/目的港', 'portOfLoading', 'portOfDischarge'),
    group('交货地', 'placeOfDelivery'), group('ETD/截关期', 'etd', 'cutOff'),
    group('货物明细（唛头/品名/HS 编码/件数/包装/毛重/净重/体积/柜号/封号/柜型/备注）', ...name, 'items.marks', 'items.hsCode', 'items.quantity', 'items.packages', 'items.grossWeight', 'items.netWeight', 'items.measurement', 'items.container', 'items.sealNumber', 'items.containerType', 'items.remark'),
    group('提单类型', 'blServiceType'), group('正本份数', 'originals'), group('运费条款', 'freightTerms'),
    group('提单抬头', 'blConsignee'), group('提单通知方', 'blNotifyParty'), group('放货条款', 'releaseClause'),
    group('特别指示', 'specialRequirements'), group('签发人/电话', 'authorizedSignature', 'issuerPhone'),
  ],
  INSURANCE_POLICY: [
    group('保单号', 'policyNumber'), group('签发日期', 'policyIssueDate'), group('被保险人', 'assured'), group('发票号', 'invoiceNumber'),
    group('币种/保险金额', 'currency', 'insuredAmount'), group('大写金额', 'insuredAmountWords'),
    group('起运港/目的港', 'portOfLoading', 'portOfDischarge'), group('船名/航次', 'vessel', 'voyageNumber'),
    group('开航日期', 'sailingDate'), group('赔款地点', 'claimPayableAt'), group('承保险别', 'insuranceCoverage'),
    group('附加险/其他附加险', 'insuranceAdditionalRisks', 'insuranceOtherRisks'), group('检验代理人', 'surveyAgent'),
    group('保险条款声明', 'insuranceDeclaration'), group('投保人签章', 'applicantSignature'),
    group('保险公司签章', 'insurerSignature'), group('备注/备注日期', 'note', 'insuranceRemarksDate'),
  ],
  CERTIFICATE_OF_ORIGIN: originGroups('CO'), GSP_FORM_A: originGroups('A'), ACFTA_FORM_E: originGroups('E'),
  INSPECTION_CERTIFICATE: inspectionGroups('inspection'), HEALTH_CERTIFICATE: inspectionGroups('health'), FUMIGATION_CERTIFICATE: inspectionGroups('fumigation'),
};

function originGroups(type: 'CO' | 'A' | 'E'): readonly FieldGroup[] {
  return [
    group('证书编号', 'certificateNumber'), group(type === 'CO' ? '出口商' : '出口商/地址/国家', 'exporter'),
    group(type === 'CO' ? '收货人' : '收货人/地址/国家', 'consignee'), group('运输方式及路线', 'transportRoute'),
    ...(type === 'CO' ? [group('目的国', 'destinationCountry')] : []),
    group(type === 'CO' ? '供签证机构使用' : '供官方使用', 'officialUse'), group('唛头', 'items.marks'),
    group(type === 'E' ? '包装件数及种类/品名及货描/HS 编码' : '包装件数及种类/品名及货描', 'items.packages', 'items.description', ...(type === 'E' ? ['items.hsCode'] as const : [])),
    ...(type === 'CO' ? [group('HS 编码', 'items.hsCode'), quantity] : [
      group(type === 'A' ? '原产地标准（P/W/F）' : '原产地标准（CTH/PSR 等）', 'items.originCriterion'),
      group(type === 'A' ? '毛重或其他数量' : '数量/单位及 FOB 金额（USD）', 'items.quantity', 'items.unit', ...(type === 'A' ? ['items.grossWeight'] as const : ['items.fobValue'] as const)),
    ]),
    group('发票号/日期', 'items.invoiceNumber', 'items.invoiceDate'),
    ...(type === 'A' ? [group('生产国', 'countryOfOrigin'), group('出口目的国', 'destinationCountry')] : []),
    group('出口商声明/地点/日期/授权签字', 'exporterDeclaration', 'declarationPlace', 'declarationDate', 'exporterSignature'),
    group('签证机构证明/地点/日期/签字盖章', 'certificationStatement', 'certificationPlace', 'certificationDate', 'certifyingAuthoritySignature'),
    ...(type === 'E' ? [group('补发', 'issuedRetroactively'), group('第三方发票', 'thirdPartyInvoicing')] : []),
  ];
}

function inspectionGroups(type: 'inspection' | 'health' | 'fumigation'): readonly FieldGroup[] {
  return [
    group('证书编号', 'certificateNumber'), group('申请人', 'applicant'), group('品名', 'commodity'),
    group(type === 'fumigation' ? '数量及件数' : '数量及重量', type === 'fumigation' ? 'quantityPackages' : 'quantityWeight'),
    group('唛头', 'shippingMarks'), group('发票号/日期', 'invoiceNumber', 'invoiceDate'),
    ...(type === 'inspection' ? [group('合同号/信用证号（二选一或均填）', 'contractNumber', 'lcNumber'),
      group('检验项目及结果明细', 'inspectionItems'), group('检验结果', 'inspectionResults')] : []),
    ...(type === 'health' ? [group('卫生检验结论', 'sanitaryResults')] : []),
    ...(type === 'fumigation' ? [group('熏蒸药剂', 'fumigant'), group('浓度', 'fumigantConcentration'), group('熏蒸时长', 'fumigationDuration'),
      group('温度', 'fumigationTemperature'), group('熏蒸结论', 'treatmentResult'), group('处理地点/日期', 'treatmentPlace', 'treatmentDate')]
      : [group('检验地点/日期', 'inspectionPlace', 'inspectionDate')]),
    group(type === 'fumigation' ? '熏蒸机构盖章及授权签字' : type === 'health' ? '检验检疫机构盖章及授权签字' : '检验机构盖章及授权签字', 'inspectionSignature'),
    group('备注', 'note'),
  ];
}

export function documentGuideFields(type: DocumentType): readonly string[] {
  return groups[type].map(entry => entry.label);
}

export function documentCompletionFields(type: DocumentType): readonly string[] {
  // Domain metadata is authoritative for specialized templates. Groups supply the
  // presentation labels; do not accidentally allow a typo or cross-type field.
  const fields: string[] = isOriginInspectionDocument(type)
    ? [...originInspectionFieldsFor(type).map(entry => entry.field), ...originInspectionItemFieldsFor(type).map(entry => `items.${entry.field}`)]
    : isTransportDocument(type)
      ? [...transportFieldsFor(type).map(entry => entry.field), ...transportItemFieldsFor(type).map(entry => `items.${entry.field}`)]
      : type === 'INSURANCE_POLICY' ? insuranceFields.map(entry => entry.field)
        : groups[type].flatMap(entry => entry.fields);
  const result = new Set<string>(fields);
  if (fields.some(field => field.startsWith('items.'))) result.add('items');
  if (fields.includes('items.description')) result.add('items.name');
  if (fields.includes('items.unitPrice')) {
    result.add('items.freeSampleReason');
    result.add('total');
  }
  return [...result];
}

export function canonicalDocumentField(path: string): string {
  return path.replace(/^items\.\d+\./, 'items.');
}

export function supportsDocumentField(type: DocumentType, path: string): boolean {
  return documentCompletionFields(type).includes(canonicalDocumentField(path));
}

export function isDocumentClarification(type: DocumentType, entry: Readonly<{ field: string; kind?: string | null; reason?: string; question?: string | null }>): boolean {
  if (!supportsDocumentField(type, entry.field) || entry.kind === 'MISSING') return false;
  if (entry.kind === 'CLARIFICATION') return true;
  // Legacy proposals did not distinguish absences from clarification. Only
  // preserve identifiable ambiguities/source warnings; validators own absences.
  return /(?:ambig|conflict|multiple|which\s+(?:product|item|row|value|one)|(?:row|product|item)\s+(?:identity|identifier)|mismatch|unsupported|source\s+(?:quote|reference)|对应|哪(?:一|个|项|行)|冲突|歧义|无法确定|来源引用)/iu.test(`${entry.reason ?? ''} ${entry.question ?? ''}`);
}

export function documentUsesBank(type: DocumentType): boolean {
  return supportsDocumentField(type, 'bankAccountVersionId');
}

export function documentBankRequired(type: DocumentType, paymentMethod: string | null): boolean {
  return documentUsesBank(type) && (type !== 'PROFORMA_INVOICE' || paymentMethod === 'TT');
}

export function isOptionalCompletionField(path: string): boolean {
  return ['note', 'authorizedSignature', 'carrierSignature', 'specialRequirements', 'items.remark', 'items.pictureDataUri'].includes(canonicalDocumentField(path));
}
